import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Users as UsersIcon,
  UserPlus,
  MagnifyingGlass,
  ArrowClockwise,
  Pause,
  Play,
  CaretLeft,
  Key,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/features/auth/store';
import {
  fetchUsers,
  updateUserStatus,
  setUserPassword,
  resendActivation,
  type ApiUser,
  type UserMemberType,
  type UserStatus,
} from '@/lib/usersApi';
import { InviteMemberModal } from '@/components/shared/InviteMemberModal';
import { getApiErrorMessage } from '@/lib/api';
import { MIN_PASSWORD_LENGTH } from '@/lib/passwordPolicy';
import { accountStatusLabel, memberAccountAction } from '@/lib/accountLifecycle';
import { ScrollLock } from '@/components/shared/ScrollLock';

const ROLE_LABEL: Record<UserMemberType, string> = {
  admin: 'Admin',
  silk: 'Silk (KC)',
  junior: 'Junior',
  pupil: 'Pupil',
  door_tenant: 'Door tenant',
  senior_clerk: 'Senior clerk',
  junior_clerk: 'Junior clerk',
  staff: 'Staff',
  solicitor: 'Solicitor',
};

const STATUS_VARIANT: Record<UserStatus, 'success' | 'warning' | 'danger' | 'default'> = {
  active: 'success',
  invited: 'warning',
  suspended: 'danger',
  inactive: 'default',
};

function fullName(u: ApiUser): string {
  return `${u.firstName} ${u.lastName}`.trim();
}

function initials(u: ApiUser): string {
  return `${u.firstName.charAt(0)}${u.lastName.charAt(0)}`.toUpperCase();
}

function lastLoginLabel(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const now = Date.now();
  const days = Math.floor((now - d.getTime()) / (24 * 60 * 60 * 1000));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function MembersPage() {
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserMemberType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<UserStatus | 'all'>('all');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pwTarget, setPwTarget] = useState<ApiUser | null>(null);
  const [newPw, setNewPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  // Debounce the search input so we don't fire a query on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const queryKey = useMemo(
    () => ['users', { search: debouncedSearch, role: roleFilter, status: statusFilter }] as const,
    [debouncedSearch, roleFilter, statusFilter],
  );

  const { data: users = [], isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      fetchUsers({
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(roleFilter !== 'all' && { memberType: roleFilter }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        limit: 200,
      }),
  });

  // Suspend, or Reactivate a suspended OR deactivated member — one action, the existing authorised
  // server operation (PATCH /users/:id). Which of the two a row offers is decided in one place
  // (lib/accountLifecycle.ts), mirroring the server's governance boundary.
  const handleAccountAction = async (u: ApiUser, nextStatus: 'suspended' | 'active') => {
    setBusyId(u.id);
    try {
      await updateUserStatus(u.id, nextStatus);
      toast.success(
        nextStatus === 'suspended'
          ? `${fullName(u)} suspended`
          : `${fullName(u)} reactivated — they can sign in again`,
      );
      qc.invalidateQueries({ queryKey: ['users'] });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not update member'));
    } finally {
      setBusyId(null);
    }
  };

  // Governance mirror of the server guard (assertCanManageMemberType): an admin
  // may set anyone's password except their own and solicitors; a senior clerk
  // may reset only the employed team (clerks + support), never a barrister/admin.
  const OPERATIONAL_STAFF: UserMemberType[] = ['senior_clerk', 'junior_clerk', 'staff'];
  const canSetPasswordFor = (u: ApiUser): boolean => {
    if (me?.id === u.id || u.memberType === 'solicitor') return false;
    if (me?.memberType === 'admin') return true;
    if (me?.memberType === 'senior_clerk') return OPERATIONAL_STAFF.includes(u.memberType);
    return false;
  };

  const handleSetPassword = async () => {
    if (!pwTarget) return;
    setPwBusy(true);
    try {
      await setUserPassword(pwTarget.id, newPw);
      toast.success(`Password set for ${fullName(pwTarget)} — they have been signed out and must use the new password`);
      setPwTarget(null);
      setNewPw('');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not set password'));
    } finally {
      setPwBusy(false);
    }
  };

  // Resend the activation link to an invited member: emails it via Resend and copies the fresh link
  // so it can be shared directly (works even if the email is delayed).
  const handleResendActivation = async (u: ApiUser) => {
    setBusyId(u.id);
    try {
      const { activationUrl } = await resendActivation(u.id);
      let copied = false;
      try { await navigator.clipboard.writeText(activationUrl); copied = true; } catch { /* clipboard blocked — email still sent */ }
      toast.success(`Activation link emailed to ${u.email}${copied ? ' and copied to your clipboard' : ''}.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not resend activation link'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="mb-2">
        <button
          onClick={() => navigate('/admin/settings')}
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <CaretLeft className="h-3 w-3" />
          Back to settings
        </button>
      </div>

      <PageHeader
        title="Manage members"
        subtitle="Invite, suspend, reactivate and review chambers members"
        actions={
          <>
            <Button variant="outline" size="md" leftIcon={<ArrowClockwise className="h-4 w-4" />} onClick={() => refetch()}>
              Refresh
            </Button>
            <Button variant="primary" size="md" leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setInviteOpen(true)}>
              Invite member
            </Button>
          </>
        }
      />

      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-center gap-3 py-4">
          <div className="relative min-w-[240px] flex-1">
            <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              className="h-9 w-full rounded-md border border-border bg-card pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-600/40"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="h-9 rounded-md border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-600/40"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as UserMemberType | 'all')}
          >
            <option value="all">All roles</option>
            <option value="admin">Admin</option>
            <option value="senior_clerk">Senior clerk</option>
            <option value="junior_clerk">Junior clerk</option>
            <option value="staff">Staff</option>
            <option value="silk">Silk (KC)</option>
            <option value="junior">Junior</option>
            <option value="pupil">Pupil</option>
            <option value="door_tenant">Door tenant</option>
            <option value="solicitor">Solicitor</option>
          </select>
          <select
            className="h-9 rounded-md border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-600/40"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as UserStatus | 'all')}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="invited">Invited</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Deactivated</option>
          </select>
          <div className="text-xs text-muted-foreground">
            {users.length} {users.length === 1 ? 'member' : 'members'}
          </div>
        </CardContent>
      </Card>

      <Card>
        <div className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-5 py-3">Member</th>
                <th className="px-5 py-3">Role</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Last login</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-sm text-muted-foreground">
                    Loading members…
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-sm text-amber-700 dark:text-amber-300">
                    Could not load members. Try refreshing.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center">
                    <UsersIcon className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                    <div className="text-sm font-medium text-foreground">No members match these filters</div>
                    <div className="text-xs text-muted-foreground">Adjust the search or invite a new member.</div>
                  </td>
                </tr>
              )}
              {users.map((u) => {
                const isMe = me?.id === u.id;
                const action = memberAccountAction(me, u);

                return (
                  <tr key={u.id} className="hover:bg-muted/40">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[10px] font-semibold text-brand-700 dark:text-brand-300">
                          {initials(u)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 truncate text-sm font-medium text-foreground">
                            {fullName(u)}
                            {isMe && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">(you)</span>}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-foreground">
                      {ROLE_LABEL[u.memberType] ?? u.memberType}
                      {u.jobTitle && (
                        <div className="text-xs text-muted-foreground">{u.jobTitle}</div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={STATUS_VARIANT[u.status]}>
                        {accountStatusLabel(u.status)}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-xs text-muted-foreground">
                      {lastLoginLabel(u.lastLoginAt)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {u.status === 'invited' && canSetPasswordFor(u) && (
                          <Button
                            variant="outline"
                            size="xs"
                            leftIcon={<EnvelopeSimple className="h-3 w-3" />}
                            onClick={() => handleResendActivation(u)}
                            disabled={busyId === u.id}
                          >
                            Resend link
                          </Button>
                        )}
                        {canSetPasswordFor(u) && (
                          <Button
                            variant="outline"
                            size="xs"
                            leftIcon={<Key className="h-3 w-3" />}
                            onClick={() => { setPwTarget(u); setNewPw(''); }}
                          >
                            Set password
                          </Button>
                        )}
                        {action && (
                          <Button
                            variant="outline"
                            size="xs"
                            leftIcon={
                              action.kind === 'reactivate' ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />
                            }
                            onClick={() => handleAccountAction(u, action.nextStatus)}
                            disabled={busyId === u.id}
                          >
                            {action.kind === 'reactivate' ? 'Reactivate' : 'Suspend'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <InviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['users'] })}
      />

      {pwTarget && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-xl">
            <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
              <Key className="h-4 w-4 text-brand-600 dark:text-brand-400" /> Set password for {fullName(pwTarget)}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Sets this member's password immediately. They will be signed out of every device and
              must sign in again with the new password. Share it with them over a secure channel —
              they can change it themselves afterwards.
            </p>
            <input
              type="text"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder={`New password (min ${MIN_PASSWORD_LENGTH} characters)`}
              autoComplete="new-password"
              className="mt-3 h-9 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground placeholder-muted-foreground focus:border-brand-500 focus:outline-none"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setPwTarget(null)} disabled={pwBusy}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Key className="h-4 w-4" />}
                onClick={handleSetPassword}
                disabled={newPw.length < MIN_PASSWORD_LENGTH || pwBusy}
              >
                Set password
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
